import { AddIcon, EditIcon, RepeatIcon, StarIcon } from '@chakra-ui/icons';
import {
    Badge, Box, Button, Card, CardBody, Checkbox, Divider, Flex, FormControl, FormErrorMessage,
    Heading, HStack, IconButton, Input, Menu, MenuButton, MenuItem, MenuList, Select, Spinner,
    Switch, Table, Tbody, Td, Text, Textarea, Th, Thead, Tr, useDisclosure, useToast, VStack
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { FiMoreVertical, FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import { hasPermission } from '../../../Utils';
import {
    createAbnTemplate, deleteAbnTemplate, getAbnTemplate, getAbnTemplates,
    restoreAbnTemplate, setDefaultAbnTemplate, updateAbnTemplate
} from '../../../api';

const PageLimit = 20;

export default function AbnTemplatesPage() {
    const toast = useToast();

    const canView = hasPermission('viewAbnTemplate');
    const canCreate = hasPermission('createAbnTemplate');
    const canUpdate = hasPermission('updateAbnTemplate');
    const canDelete = hasPermission('deleteAbnTemplate');
    const canRestore = hasPermission('restoreAbnTemplate');
    const canSetDefault = hasPermission('setDefaultAbnTemplate');

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [q, setQ] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [sort, setSort] = useState('-createdAt');

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [restoringId, setRestoringId] = useState(null);

    const [form, setForm] = useState({ version: 1, subject: '', html: '', isActive: true, isDefault: false });
    const [errors, setErrors] = useState({});
    const [showErrors, setShowErrors] = useState(false);

    const queryString = useMemo(() => {
        const p = new URLSearchParams();
        p.set('page', String(page));
        p.set('limit', String(PageLimit));
        if (q) p.set('q', q);
        if (isActiveFilter) p.set('isActive', isActiveFilter);
        if (includeDeleted) p.set('includeDeleted', 'true');
        if (sort) p.set('sort', sort);
        return p.toString();
    }, [page, q, isActiveFilter, includeDeleted, sort]);

    const load = async () => {
        if (!canView) return;
        try {
            setLoading(true);
            const res = await getAbnTemplates(queryString);
            if (res.success) {
                const payload = res?.data || res;
                setItems(payload.items || []);
                setTotal(payload.total || 0);
                setPage(payload.page || 1);
                setPages(payload.pages || 1);
            } else {
                setError(res.error || res.message);
            }
        } catch (err) {
            toast({ status: 'error', title: 'Load failed', description: err.message });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-line */ }, [queryString, canView]);

    const resetForm = () => {
        setForm({ version: 1, subject: '', html: '', isActive: true, isDefault: false });
        setErrors({}); setShowErrors(false); setEditing(null);
    };

    const openCreate = () => { if (!canCreate) return toast({ status: 'warning', title: 'No permission' }); resetForm(); onOpen(); };

    const openEdit = async (doc) => {
        if (!canUpdate) return toast({ status: 'warning', title: 'No permission' });
        setEditing(doc);
        try {
            const res = await getAbnTemplate(doc._id);
            const d = res?.data || doc;
            setForm({ version: d.version || 1, subject: d.subject || '', html: d.html || '', isActive: !!d.isActive, isDefault: !!d.isDefault });
        } catch { setForm({ version: doc.version || 1, subject: doc.subject || '', html: doc.html || '', isActive: !!doc.isActive, isDefault: !!doc.isDefault }); }
        setErrors({}); setShowErrors(false); onOpen();
    };

    // submit-only validation
    const validate = (f) => {
        const e = {};
        if (!f.subject || f.subject.trim().length < 2) e.subject = 'Subject is required';
        if (!f.html || f.html.trim().length < 1) e.html = 'HTML body is required';
        if (!f.version || +f.version < 1) e.version = 'Version must be >= 1';
        return e;
    };

    const updateForm = (patch) => setForm(prev => ({ ...prev, ...patch }));

    const onSave = async () => {
        const e = validate(form);
        setErrors(e); setShowErrors(true);
        if (Object.keys(e).length) { toast({ status: 'error', title: 'Please fix form errors' }); return; }
        try {
            setSaving(true);
            const payload = { ...form, version: Number(form.version) || 1 };
            const res = editing ? await updateAbnTemplate(editing._id, payload) : await createAbnTemplate(payload);
            if (res?.success === false) throw new Error(res?.message || 'Save failed');
            toast({ status: 'success', title: editing ? 'Updated' : 'Created' });
            onClose(); resetForm(); load();
        } catch (err) {
            toast({ status: 'error', title: 'Save failed', description: err.message });
        } finally {
            setSaving(false);
        }
    };

    const onSoftDelete = async (id) => {
        if (!canDelete) return toast({ status: 'warning', title: 'No permission' });
        try {
            setDeletingId(id);
            const res = await deleteAbnTemplate(id, 'User requested removal');
            if (res?.success === false) throw new Error(res?.message || 'Delete failed');
            toast({ status: 'success', title: 'Soft deleted' }); load();
        } catch (err) {
            toast({ status: 'error', title: 'Delete failed', description: err.message });
        } finally { setDeletingId(null); }
    };

    const onRestore = async (id) => {
        if (!canRestore) return toast({ status: 'warning', title: 'No permission' });
        try {
            setRestoringId(id);
            const res = await restoreAbnTemplate(id);
            if (res?.success === false) throw new Error(res?.message || 'Restore failed');
            toast({ status: 'success', title: 'Restored' }); load();
        } catch (err) {
            toast({ status: 'error', title: 'Restore failed', description: err.message });
        } finally { setRestoringId(null); }
    };

    const onSetDefault = async (id) => {
        if (!canSetDefault) return toast({ status: 'warning', title: 'No permission' });
        try {
            const res = await setDefaultAbnTemplate(id);
            if (res?.success === false) throw new Error(res?.message || 'Failed to set default');
            toast({ status: 'success', title: 'Default set' }); load();
        } catch (err) { toast({ status: 'error', title: 'Failed', description: err.message }); }
    };

    if (!canView) return <Box p={5}><Text>Access denied.</Text></Box>;
    if (error) return <Box p={5}><Text color="red.500">{error}</Text></Box>;

    return (
        <Box p={5}>
            <Flex justify="space-between" align="center" mb={4}>
                <Heading size="lg">ABN Templates</Heading>
                <HStack>
                    {canCreate && <Button size={'xs'} leftIcon={<AddIcon />} colorScheme="blue" onClick={openCreate}>New Template</Button>}
                    <IconButton size={'xs'} aria-label="refresh" icon={<RepeatIcon />} onClick={load} />
                </HStack>
            </Flex>

            <Card mb={4}>
                <CardBody>
                    <HStack spacing={3} flexWrap="wrap">
                        <Input placeholder="Search subject/html..." value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} maxW="320px" />
                        <Select value={isActiveFilter} onChange={(e) => { setPage(1); setIsActiveFilter(e.target.value); }} maxW="200px">
                            <option value="">Any status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </Select>
                        <Select value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }} maxW="220px">
                            <option value="-createdAt">Newest</option>
                            <option value="subject">Subject A→Z</option>
                            <option value="-subject">Subject Z→A</option>
                            <option value="version,-updatedAt">Version</option>
                        </Select>
                        <Checkbox isChecked={includeDeleted} onChange={(e) => { setPage(1); setIncludeDeleted(e.target.checked); }}>
                            Include deleted
                        </Checkbox>
                    </HStack>
                </CardBody>
            </Card>

            <Card>
                <CardBody p={0}>
                    {loading ? (
                        <Flex align="center" justify="center" py={10}><Spinner /></Flex>
                    ) : (
                        <Table size="sm">
                            <Thead>
                                <Tr>
                                    <Th w="36px">#</Th>
                                    <Th>Subject</Th>
                                    <Th>Version</Th>
                                    <Th>Status</Th>
                                    <Th>Default</Th>
                                    <Th w="140px">Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {items.map((it, idx) => {
                                    const row = (page - 1) * PageLimit + idx + 1;
                                    return (
                                        <Tr key={it._id} _hover={{ bg: 'gray.50' }}>
                                            <Td>{row}</Td>
                                            <Td>
                                                <HStack spacing={2}>
                                                    <Text fontWeight="semibold">{it.subject || '(no subject)'}</Text>
                                                    {it.deletedAt ? <Badge colorScheme="red">Deleted</Badge> : null}
                                                </HStack>
                                                {/* {it.html ? <Text fontSize="xs" color="gray.600" noOfLines={1} dangerouslySetInnerHTML={{ __html: it.html }} /> : null} */}
                                            </Td>
                                            <Td>{it.version || 1}</Td>
                                            <Td>{it.isActive ? <Badge colorScheme="green">Active</Badge> : <Badge>Inactive</Badge>}</Td>
                                            <Td>
                                                {it.isDefault
                                                    ? <HStack><StarIcon color="yellow.400" /><Text>Default</Text></HStack>
                                                    : (canSetDefault && !it.deletedAt ? <Button size="xs" onClick={() => onSetDefault(it._id)}>Make default</Button> : '—')}
                                            </Td>
                                            <Td>
                                                <HStack spacing={1} justify="flex-end">
                                                    {!it.deletedAt ? (
                                                        <>
                                                            {canUpdate && <IconButton aria-label="edit" size="sm" icon={<EditIcon />} onClick={() => openEdit(it)} />}
                                                            {canDelete && (
                                                                <Menu>
                                                                    <MenuButton as={IconButton} aria-label="more" size="sm" icon={<FiMoreVertical />} />
                                                                    <MenuList>
                                                                        <MenuItem icon={<FiTrash2 />} onClick={() => onSoftDelete(it._id)} isDisabled={deletingId === it._id}>
                                                                            {deletingId === it._id ? 'Deleting…' : 'Soft delete'}
                                                                        </MenuItem>
                                                                    </MenuList>
                                                                </Menu>
                                                            )}
                                                        </>
                                                    ) : (
                                                        canRestore && (
                                                            <Button size="sm" leftIcon={<FiRotateCcw />} onClick={() => onRestore(it._id)} isLoading={restoringId === it._id}>
                                                                Restore
                                                            </Button>
                                                        )
                                                    )}
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                                {!items.length && !loading && (
                                    <Tr><Td colSpan={6}><Flex justify="center" py={10}><Text color="gray.500">No templates</Text></Flex></Td></Tr>
                                )}
                            </Tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            <Flex align="center" justify="space-between" mt={4}>
                <Text fontSize="sm" color="gray.600">{total} total • Page {page} of {pages}</Text>
                <HStack>
                    <Button size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} isDisabled={page <= 1}>Prev</Button>
                    <Button size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} isDisabled={page >= pages}>Next</Button>
                </HStack>
            </Flex>

            {isOpen && (
                <Box position="fixed" inset="0" bg="blackAlpha.500" display="flex" alignItems="center" justifyContent="center" zIndex={1000}>
                    <Card w="900px" maxW="96vw">
                        <CardBody>
                            <Heading size="md" mb={3}>{editing ? 'Edit ABN Template' : 'New ABN Template'}</Heading>
                            <VStack align="stretch" spacing={3}>
                                <HStack>
                                    <FormControl isInvalid={showErrors && !!errors.subject} flex="1">
                                        <Text fontSize="sm" mb={1}>Subject *</Text>
                                        <Input value={form.subject} onChange={e => updateForm({ subject: e.target.value })} />
                                        {showErrors && <FormErrorMessage>{errors.subject}</FormErrorMessage>}
                                    </FormControl>
                                    <FormControl isInvalid={showErrors && !!errors.version} w="160px">
                                        <Text fontSize="sm" mb={1}>Version *</Text>
                                        <Input type="number" value={form.version} onChange={e => updateForm({ version: e.target.value })} />
                                        {showErrors && <FormErrorMessage>{errors.version}</FormErrorMessage>}
                                    </FormControl>
                                    <HStack>
                                        <Text>Active</Text>
                                        <Switch isChecked={form.isActive} onChange={e => updateForm({ isActive: e.target.checked })} />
                                    </HStack>
                                    <HStack>
                                        <Text>Default</Text>
                                        <Switch isChecked={form.isDefault} onChange={e => updateForm({ isDefault: e.target.checked })} />
                                    </HStack>
                                </HStack>

                                <FormControl isInvalid={showErrors && !!errors.html}>
                                    <Text fontSize="sm" mb={1}>Body (HTML) *</Text>
                                    <Textarea rows={10} value={form.html} onChange={e => updateForm({ html: e.target.value })} placeholder="<p>…</p>" />
                                    {showErrors && <FormErrorMessage>{errors.html}</FormErrorMessage>}
                                </FormControl>
                            </VStack>

                            <Divider my={4} />
                            <HStack justify="flex-end">
                                <Button size={'xs'} variant="ghost" onClick={() => { onClose(); resetForm(); }} isDisabled={saving}>Cancel</Button>
                                <Button size={'xs'} colorScheme="blue" onClick={onSave} isLoading={saving} isDisabled={(editing && !canUpdate) || (!editing && !canCreate)}>
                                    {editing ? 'Save Changes' : 'Create'}
                                </Button>
                            </HStack>
                        </CardBody>
                    </Card>
                </Box>
            )}
        </Box>
    );
}